import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import type { TLog } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination } from '@/core/types'
import { useScrapStore } from '../../store/details'
import { storeToRefs } from 'pinia'
import { useCatalogStore } from '@/core/store/catalogs'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const selectedScrap = ref<TLog[]>([])
  const form = ref<TLog>({})
  const store = useScrapStore()
  const { scrap } = storeToRefs(store)
  const storeCatalog = useCatalogStore()
  const { catalogs } = storeToRefs(storeCatalog)
  const filters = ref<ToQueryStringFilters>({
    param: '',
    dates: {
      scrap_used_date: [],
    },
    galvanized_grade_id: [],
    gauge_id: [],
    hardness_id: [],
    scrap_used: [0],
    order_by: 'scrap_used,asc',
  })

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: ['total_scrap_feet'],
  })

  const modulePagination = usePagination()

  const moduleQuery = useModuleQuery({
    currentPage: modulePagination.currentPage,
    filters: {
      loadRecords: moduleQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: true,
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        modulePagination.setPagination(pagination)
      },
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['scrap_used_date', 'scrap_used', 'total_scrap_feet'],
  })

  const onEdit = async (payload: TLog) => {
    await moduleQuery.editRecord.mutateAsync(payload)
  }

  const onRequestCatalog = async (payload: number[]) => {
    await moduleQuery.requestCatalog.mutateAsync({ ids: payload })
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)
  const mills = computed(() => moduleQuery?.millsCatalog.data?.value)

  const isLoading = computed(() => moduleQuery.loadRecords.isLoading.value)
  const isLoadingMills = computed(
    () => moduleQuery.millsCatalog.isLoading.value,
  )

  const isLoadingCategories = computed(
    () => moduleQuery.requestCatalog.isLoading.value,
  )

  return {
    tableData,
    filters,
    orderByOptions,
    rows,
    form,
    isLoading,
    selectedScrap,
    scrap,
    catalogs,
    isLoadingCategories,
    mills,
    isLoadingMills,
    modulePagination: toReactive(modulePagination),
    onRequestCatalog,
    onEdit,
  }
})

export { useProvideModule, useModule }
